import { useState } from 'react';
import { useGetContract } from './useGetContract';
import { DEFAULT_GAS_LIMIT, ContractNames, PROGRAM_NAMES, PROGRAM_PRICES_WEI, PROGRAM_FEE_WEI } from '../constants';
import { increaseByPercent } from '../numbers';
import { useWeb3React } from '@web3-react/core';
import { callNotification } from '../notification';
import { parseErrorToUserReadableMessage } from '../format';
import useTokenPrice from './useTokenPrice';
import config from '../config';
import { findNextMatrixLevel } from '../program';

export const useUpgradeLvl = () => {
  const [isLoadingUpgrade, setIsLoadingUpgrade] = useState(false);
  const { getContract } = useGetContract();
  const { account, provider } = useWeb3React();
  const { fetchTokenAmount } = useTokenPrice();

  const upgradeLvl = async (levelsX2 = [], levelsX4 = [], activateIncome = false) => {
    if (!isLoadingUpgrade) {
      setIsLoadingUpgrade(true);

      try {
        const matrixContract = await getContract(ContractNames.MATRIX_X2X4);
        if (levelsX2.length) {
          const nextX2 = await findNextMatrixLevel(matrixContract, account, true);
          if (!nextX2) {
            throw new Error('All Duo levels are already activated');
          }
          levelsX2 = [nextX2];
        }
        if (levelsX4.length) {
          const nextX4 = await findNextMatrixLevel(matrixContract, account, false);
          if (!nextX4) {
            throw new Error('All Quattro levels are already activated');
          }
          levelsX4 = [nextX4];
        }
      } catch (e) {
        setIsLoadingUpgrade(false);
        callNotification({ type: 'error', message: parseErrorToUserReadableMessage(e) });
        return Promise.reject(e);
      }

      if (config.stand === 'prod') {
        // PROD режим - через роутер с полной логикой
        let priceX2 = 0;
        let priceX4 = 0;
        let feeX2 = 0;
        let feeX4 = 0;
        let passiveX2 = 0;
        let passiveX4 = 0;

        if (levelsX2.length) {
          passiveX2 = activateIncome
            ? levelsX2.reduce((acc, item) => {
                if (!!PROGRAM_PRICES_WEI[PROGRAM_NAMES.X2][item - 1]) {
                  return (acc = acc + PROGRAM_PRICES_WEI[PROGRAM_NAMES.X2][item - 1]);
                }
                return acc;
              }, 0) * 0.2
            : 0;

          passiveX4 = activateIncome
            ? levelsX4.reduce((acc, item) => {
                if (!!PROGRAM_PRICES_WEI[PROGRAM_NAMES.X4][item - 1]) {
                  return (acc = acc + PROGRAM_PRICES_WEI[PROGRAM_NAMES.X4][item - 1]);
                }
                return acc;
              }, 0) * 0.2
            : 0;

          priceX2 = levelsX2.reduce((acc, item) => {
            if (!!PROGRAM_PRICES_WEI[PROGRAM_NAMES.X2][item - 1]) {
              return (acc =
                acc + PROGRAM_PRICES_WEI[PROGRAM_NAMES.X2][item - 1] + PROGRAM_FEE_WEI[PROGRAM_NAMES.X2][item - 1]);
            }
            return acc;
          }, 0);

          feeX2 = levelsX2.reduce((acc, item) => {
            if (!!PROGRAM_PRICES_WEI[PROGRAM_NAMES.X2][item - 1]) {
              return (acc = acc + PROGRAM_FEE_WEI[PROGRAM_NAMES.X2][item - 1]);
            }
            return acc;
          }, 0);
        }

        if (levelsX4.length) {
          priceX4 = levelsX4.reduce((acc, item) => {
            if (!!PROGRAM_PRICES_WEI[PROGRAM_NAMES.X4][item - 1]) {
              return (acc =
                acc + PROGRAM_PRICES_WEI[PROGRAM_NAMES.X4][item - 1] + PROGRAM_FEE_WEI[PROGRAM_NAMES.X4][item - 1]);
            }
            return acc;
          }, 0);

          feeX4 = levelsX4.reduce((acc, item) => {
            if (!!PROGRAM_PRICES_WEI[PROGRAM_NAMES.X4][item - 1]) {
              return (acc = acc + PROGRAM_FEE_WEI[PROGRAM_NAMES.X4][item - 1]);
            }
            return acc;
          }, 0);
        }

        let totalPrice = priceX2 + priceX4;

        // Если activateIncome = true, увеличиваем цену на 20%
        if (activateIncome) {
          totalPrice = totalPrice * 1.2;
        }

        try {
          const contract = await getContract(ContractNames.ROUTER);
          const feeForContract = ((feeX2 + feeX4) / 1e18) * 0.45 * 0.9;
          let gasEstimate = 0;
          let minPollyReceive = 0;
          let minPollyX2Receive = 0;
          let minPollyX4Receive = 0;

          const minPolly = await fetchTokenAmount(config.contractPollyPool, provider, feeForContract);
          const minPollyX2 = await fetchTokenAmount(config.contractPollyPool, provider, passiveX2);
          const minPollyX4 = await fetchTokenAmount(config.contractPollyPool, provider, passiveX4);
          minPollyReceive = BigInt(Math.floor(minPolly));
          minPollyX2Receive = activateIncome ? BigInt(Math.floor(minPollyX2)) : 0;
          minPollyX4Receive = activateIncome ? BigInt(Math.floor(minPollyX4)) : 0;

          gasEstimate = await contract.estimateGas.buyLevelsX2X4V2(
            account,
            levelsX2,
            levelsX4,
            minPollyReceive,
            activateIncome,
            minPollyX2Receive,
            minPollyX4Receive,
            {
              value: BigInt(totalPrice),
            },
          );

          return await contract.buyLevelsX2X4V2(
            account,
            levelsX2,
            levelsX4,
            minPollyReceive,
            activateIncome,
            minPollyX2Receive,
            minPollyX4Receive,
            {
              value: BigInt(totalPrice),
              gasLimit: parseInt(gasEstimate) ? increaseByPercent(gasEstimate) : DEFAULT_GAS_LIMIT,
            },
          );
        } catch (e) {
          callNotification({ type: 'error', message: parseErrorToUserReadableMessage(e) });
          return Promise.reject(e);
        } finally {
          setIsLoadingUpgrade(false);
        }
      } else {
        // DEV режим - напрямую через контракт матрицы
        try {
          const contract = await getContract(ContractNames.MATRIX_X2X4);

          // Покупаем уровни последовательно
          const allLevels = [
            ...levelsX2.map((level) => ({ matrix: 1, level })), // matrix: 1 = X2
            ...levelsX4.map((level) => ({ matrix: 2, level })), // matrix: 2 = X4
          ];

          for (const { matrix, level } of allLevels) {
            const price = BigInt(PROGRAM_PRICES_WEI[matrix === 1 ? PROGRAM_NAMES.X2 : PROGRAM_NAMES.X4][level - 1]);

            // Проверяем баланс пользователя
            const balance = await provider.getBalance(account);
            if (BigInt(balance.toString()) < price) {
              throw new Error(`Insufficient balance. Required: ${price.toString()}, Available: ${balance.toString()}`);
            }

            let gasEstimate = 0;

            try {
              // Пытаемся оценить газ перед отправкой
              gasEstimate = await contract.estimateGas.buyNewLevelFor(account, matrix, level, {
                value: price,
              });
            } catch (estimateError) {
              console.error('Gas estimation failed:', estimateError);
              // Выбрасываем ошибку с более понятным сообщением
              throw new Error(
                'Transaction will fail. Possible reasons: level already activated, previous level not activated, or contract error',
              );
            }

            // Отправляем транзакцию с gasLimit
            const tx = await contract.buyNewLevelFor(account, matrix, level, {
              value: price,
              gasLimit: parseInt(gasEstimate) ? increaseByPercent(gasEstimate) : DEFAULT_GAS_LIMIT,
            });

            // Ждем подтверждения перед переходом к следующему уровню
            await tx.wait();
          }

          return true;
        } catch (e) {
          console.log(e);
          callNotification({ type: 'error', message: parseErrorToUserReadableMessage(e) });
          return Promise.reject(e);
        } finally {
          setIsLoadingUpgrade(false);
        }
      }
    }
  };

  return {
    upgradeLvl,
    isLoadingUpgrade,
  };
};
